import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/firebase/authorityClient";
import {
  isMonetaryAuthorityQuotesFeatureEnabled,
  isMonetaryAuthoritySettlementFeatureEnabled,
} from "@/config/runtimeConfig";
import { withUiDeadline } from "@/utils/uiDeadline";

const SCHEMA_VERSION = 1;
const AMOUNT_SCALE = 9;
const MAX_ATOMIC_DIGITS = 60;
const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const OPAQUE_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const SHA256_PATTERN = /^[a-f0-9]{64}$/;
const ORG_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const PUBLIC_IDENTIFIER_PATTERN = /^[a-z0-9][a-z0-9_-]{2,63}$/;
const CONTROL_CHARACTERS =
  /[\u0000-\u001f\u007f-\u009f\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/;
const SAVINGS_DIRECTIONS = new Set(["to-savings", "to-current"]);
const LIFETIME_RESULT_KEYS = Object.freeze([
  "schemaVersion",
  "eventId",
  "kind",
  "direction",
  "orgId",
  "amountAtomic",
  "currentAvailableAtomic",
  "savingsAvailableAtomic",
  "circulatingSupplyAtomic",
  "completedAt",
]);

export class MonetaryAuthorityServiceError extends Error {
  constructor(
    code = "unknown",
    message = "Unit payments are unavailable.",
    reason = null,
  ) {
    super(message);
    this.name = "MonetaryAuthorityServiceError";
    this.code = code;
    // A stable, allowlisted classification of the server's public failure.
    // The raw server message is never exposed; callers map `reason` to their
    // own copy (for example insufficient funds versus a closed economy).
    this.reason = reason;
  }
}

// Exact public messages emitted by functions/monetaryAuthority (service.js,
// handlers.js, firestoreRepository.js, lotEngine.js and lotRecords.js).
// Anything else stays unclassified.
const SERVER_FAILURE_REASONS = Object.freeze({
  "failed-precondition": Object.freeze({
    "The Wallet has insufficient funds.": "insufficient-funds",
    "Cross-organisation trade is not available.": "economy-closed",
    "The quote has expired.": "quote-expired",
    "The quote has already been used.": "quote-used",
    "Self-settlement is not available.": "self-transfer",
    "The sender membership cannot trade.": "sender-cannot-trade",
    "The sender Wallet is unavailable.": "sender-wallet-unavailable",
    "The recipient membership cannot trade.": "recipient-unavailable",
    "The recipient Wallet is unavailable.": "recipient-unavailable",
    "An account cannot pay for its own item.": "self-transfer",
    "The recipient's account limits are full.": "recipient-caps-full",
    "Expired Units must be reconciled before this monetary action.":
      "expiry-maintenance-required",
    "Expired Units must be reconciled before settlement.":
      "expiry-maintenance-required",
    "This move would exceed the Savings account limit.": "savings-cap-exceeded",
    "This move would exceed the Current account limit.": "current-cap-exceeded",
    "The Wallet has insufficient funds in the selected balance.":
      "insufficient-funds",
    "The current Wallet balance has insufficient unexpired Units.":
      "insufficient-funds",
  }),
  "permission-denied": Object.freeze({
    "The recipient is unavailable.": "recipient-unavailable",
    "The item is unavailable.": "item-unavailable",
  }),
  unavailable: Object.freeze({
    "Monetary authority is unavailable.": "feature-disabled",
  }),
});

function classifyServerFailure(code, message, abortedReason) {
  // Every aborted quote or settlement failure means the quoted binding (rate,
  // trade authorization, Wallet or listing) changed; a new quote is required.
  // An aborted Savings move or expiry tidy-up only lost a race and is safe to
  // repeat with the same operation.
  if (code === "aborted") return abortedReason;
  const reasons = SERVER_FAILURE_REASONS[code];
  if (!reasons || typeof message !== "string") return null;
  return Object.hasOwn(reasons, message) ? reasons[message] : null;
}

function fail(code, message = "Unit payments are unavailable.") {
  throw new MonetaryAuthorityServiceError(code, message);
}

function isPlainRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactKeys(value, expectedKeys) {
  if (!isPlainRecord(value)) return false;
  const ownKeys = Reflect.ownKeys(value);
  const descriptors = Object.getOwnPropertyDescriptors(value);
  const actualKeys = ownKeys.filter((key) => typeof key === "string").sort();
  const sortedExpected = [...expectedKeys].sort();
  return (
    ownKeys.every(
      (key) =>
        typeof key === "string" &&
        descriptors[key]?.enumerable === true &&
        Object.prototype.hasOwnProperty.call(descriptors[key], "value"),
    ) &&
    actualKeys.length === sortedExpected.length &&
    actualKeys.every((key, index) => key === sortedExpected[index])
  );
}

function requireExactKeys(value, expectedKeys, code = "invalid-argument") {
  if (!hasExactKeys(value, expectedKeys)) fail(code);
  return value;
}

function normalizeOperationId(value) {
  if (typeof value !== "string" || !OPERATION_ID_PATTERN.test(value)) {
    fail("invalid-argument", "The payment operation is invalid.");
  }
  return value;
}

function normalizeOpaqueId(value, code = "invalid-argument") {
  if (typeof value !== "string" || !OPAQUE_ID_PATTERN.test(value)) fail(code);
  return value;
}

function normalizeOrgId(value, code = "invalid-argument") {
  if (typeof value !== "string" || !ORG_ID_PATTERN.test(value)) fail(code);
  return value;
}

function normalizePublicIdentifier(value) {
  if (
    typeof value !== "string" ||
    !PUBLIC_IDENTIFIER_PATTERN.test(value)
  ) {
    fail("invalid-argument", "The payment recipient is invalid.");
  }
  return value;
}

function normalizePlacementRef(value) {
  if (typeof value !== "string" || !SHA256_PATTERN.test(value)) {
    fail("invalid-argument", "The payment item is invalid.");
  }
  return value;
}

function normalizeAtomic(value, code = "invalid-argument") {
  if (
    typeof value !== "string" ||
    !/^[1-9]\d*$/.test(value) ||
    value.length > MAX_ATOMIC_DIGITS
  ) {
    fail(code);
  }
  return value;
}

function normalizeText(value, maximum, code = "invalid-response") {
  if (
    typeof value !== "string" ||
    value.length < 1 ||
    value.length > maximum ||
    value !== value.trim() ||
    CONTROL_CHARACTERS.test(value)
  ) {
    fail(code);
  }
  return value;
}

function normalizeTimestamp(value, code = "invalid-response") {
  if (!Number.isSafeInteger(value) || value < 1) fail(code);
  return value;
}

function normalizeParty(value) {
  const code = "invalid-response";
  requireExactKeys(value, ["orgId", "name", "currency"], code);
  return Object.freeze({
    orgId: normalizeOrgId(value.orgId, code),
    name: normalizeText(value.name, 120, code),
    currency: normalizeText(value.currency, 16, code),
  });
}

function normalizeAmounts(value) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    ["debitAtomic", "creditAtomic", "amountScale"],
    code,
  );
  if (value.amountScale !== AMOUNT_SCALE) fail(code);
  return Object.freeze({
    debitAtomic: normalizeAtomic(value.debitAtomic, code),
    creditAtomic: normalizeAtomic(value.creditAtomic, code),
    amountScale: AMOUNT_SCALE,
  });
}

function normalizePayerCandidate(value) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    ["orgId", "name", "currency", "isActiveOrganisation"],
    code,
  );
  if (typeof value.isActiveOrganisation !== "boolean") fail(code);
  return Object.freeze({
    orgId: normalizeOrgId(value.orgId, code),
    name: normalizeText(value.name, 120, code),
    currency: normalizeText(value.currency, 16, code),
    isActiveOrganisation: value.isActiveOrganisation,
  });
}

function normalizePayerSelection(value, payer) {
  const code = "invalid-response";
  requireExactKeys(value, ["requiresChoice", "candidates"], code);
  if (
    typeof value.requiresChoice !== "boolean" ||
    !Array.isArray(value.candidates) ||
    value.candidates.length < 1 ||
    value.candidates.length > 25
  ) {
    fail(code);
  }
  const candidates = value.candidates.map(normalizePayerCandidate);
  const candidateIds = candidates.map(({ orgId }) => orgId);
  const selectedCandidate = candidates.find(
    ({ orgId }) => orgId === payer.orgId,
  );
  if (
    new Set(candidateIds).size !== candidateIds.length ||
    !selectedCandidate ||
    selectedCandidate.name !== payer.name ||
    selectedCandidate.currency !== payer.currency ||
    candidates.filter(({ isActiveOrganisation }) => isActiveOrganisation)
      .length > 1 ||
    value.requiresChoice !== (candidates.length > 1)
  ) {
    fail(code);
  }
  return Object.freeze({
    requiresChoice: value.requiresChoice,
    candidates: Object.freeze(candidates),
  });
}

function normalizeListing(value, request, amounts) {
  const code = "invalid-response";
  requireExactKeys(value, ["listingId", "quantity", "unitPriceAtomic"], code);
  const unitPriceAtomic = normalizeAtomic(value.unitPriceAtomic, code);
  if (
    value.listingId !== request.listingId ||
    value.quantity !== request.quantity ||
    BigInt(unitPriceAtomic) * BigInt(value.quantity) !==
      BigInt(amounts.creditAtomic)
  ) {
    fail(code);
  }
  return Object.freeze({
    listingId: value.listingId,
    quantity: value.quantity,
    unitPriceAtomic,
  });
}

function normalizeQuoteResponse(
  value,
  request,
  expectedKind,
  { expectedPayeeOrgId = null } = {},
) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    [
      "schemaVersion",
      "quoteId",
      "kind",
      "expiresAt",
      "payer",
      "payee",
      "amounts",
      "listing",
      "payerSelection",
    ],
    code,
  );
  if (value.schemaVersion !== SCHEMA_VERSION || value.kind !== expectedKind) {
    fail(code);
  }
  const payer = normalizeParty(value.payer);
  const payee = normalizeParty(value.payee);
  const amounts = normalizeAmounts(value.amounts);
  let listing = null;
  let payerSelection = null;

  if (expectedKind === "listing-purchase") {
    if (value.listing === null || value.payerSelection === null) fail(code);
    listing = normalizeListing(value.listing, request, amounts);
    payerSelection = normalizePayerSelection(value.payerSelection, payer);
    if (request.payerOrgId !== null && payer.orgId !== request.payerOrgId) {
      fail(code);
    }
  } else {
    if (value.listing !== null || value.payerSelection !== null) fail(code);
    // An item transfer names no payee: the server resolves the item owner's
    // placement organisation privately, so only the payer and debit are known.
    if (
      payer.orgId !== request.fromOrgId ||
      (expectedPayeeOrgId !== null && payee.orgId !== expectedPayeeOrgId) ||
      amounts.debitAtomic !== request.amountAtomic
    ) {
      fail(code);
    }
  }
  if (
    payer.orgId === payee.orgId &&
    (payer.name !== payee.name ||
      payer.currency !== payee.currency ||
      amounts.debitAtomic !== amounts.creditAtomic)
  ) {
    fail(code);
  }

  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    quoteId: normalizeOpaqueId(value.quoteId, code),
    kind: expectedKind,
    expiresAt: normalizeTimestamp(value.expiresAt, code),
    payer,
    payee,
    amounts,
    listing,
    payerSelection,
  });
}

function normalizePurchaseRequest(value) {
  requireExactKeys(value, ["operationId", "listingId", "quantity", "payerOrgId"]);
  if (
    !Number.isSafeInteger(value.quantity) ||
    value.quantity < 1 ||
    value.quantity > 25
  ) {
    fail("invalid-argument");
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    listingId: normalizeOpaqueId(value.listingId),
    quantity: value.quantity,
    payerOrgId:
      value.payerOrgId === null ? null : normalizeOrgId(value.payerOrgId),
  });
}

function normalizeTransferRequest(value) {
  requireExactKeys(value, [
    "operationId",
    "fromOrgId",
    "recipientPublicId",
    "toOrgId",
    "amountAtomic",
  ]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    fromOrgId: normalizeOrgId(value.fromOrgId),
    recipientPublicId: normalizePublicIdentifier(value.recipientPublicId),
    toOrgId: normalizeOrgId(value.toOrgId),
    amountAtomic: normalizeAtomic(value.amountAtomic),
  });
}

function normalizeItemTransferRequest(value) {
  requireExactKeys(value, [
    "operationId",
    "fromOrgId",
    "placementRef",
    "amountAtomic",
  ]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    fromOrgId: normalizeOrgId(value.fromOrgId),
    placementRef: normalizePlacementRef(value.placementRef),
    amountAtomic: normalizeAtomic(value.amountAtomic),
  });
}

function normalizeSettlementRequest(value) {
  requireExactKeys(value, ["operationId", "quoteId"]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    quoteId: normalizeOpaqueId(value.quoteId),
  });
}

function normalizeSavingsMoveRequest(value) {
  requireExactKeys(value, ["operationId", "orgId", "direction", "amountAtomic"]);
  if (!SAVINGS_DIRECTIONS.has(value.direction)) fail("invalid-argument");
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    orgId: normalizeOrgId(value.orgId),
    direction: value.direction,
    amountAtomic: normalizeAtomic(value.amountAtomic),
  });
}

function normalizeExpiryRequest(value) {
  requireExactKeys(value, ["operationId", "orgId"]);
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    operationId: normalizeOperationId(value.operationId),
    orgId: normalizeOrgId(value.orgId),
  });
}

function normalizeBalanceAtomic(value) {
  if (
    typeof value !== "string" ||
    !/^(?:0|[1-9]\d*)$/.test(value) ||
    value.length > MAX_ATOMIC_DIGITS
  ) {
    fail("invalid-response");
  }
  return value;
}

// Shared result of moveUnitSavings ("savings-move") and reconcileUnitExpiry
// ("expiry"). An expiry tidy-up may remove nothing, in which case the server
// records no event.
function normalizeLifetimeResponse(value, request, expectedKind) {
  const code = "invalid-response";
  requireExactKeys(value, LIFETIME_RESULT_KEYS, code);
  const isMove = expectedKind === "savings-move";
  const amountAtomic = normalizeBalanceAtomic(value.amountAtomic);
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    value.kind !== expectedKind ||
    value.orgId !== request.orgId ||
    value.direction !== (isMove ? request.direction : null) ||
    (isMove && amountAtomic !== request.amountAtomic) ||
    (value.eventId === null) !== (amountAtomic === "0") ||
    (value.eventId !== null &&
      (typeof value.eventId !== "string" ||
        !SHA256_PATTERN.test(value.eventId)))
  ) {
    fail(code);
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    eventId: value.eventId,
    kind: expectedKind,
    direction: value.direction,
    orgId: value.orgId,
    amountAtomic,
    currentAvailableAtomic: normalizeBalanceAtomic(value.currentAvailableAtomic),
    savingsAvailableAtomic: normalizeBalanceAtomic(value.savingsAvailableAtomic),
    circulatingSupplyAtomic: normalizeBalanceAtomic(
      value.circulatingSupplyAtomic,
    ),
    completedAt: normalizeTimestamp(value.completedAt, code),
  });
}

function normalizeSettlementResponse(value, request) {
  const code = "invalid-response";
  requireExactKeys(
    value,
    [
      "schemaVersion",
      "settlementId",
      "quoteId",
      "kind",
      "payerOrgId",
      "payeeOrgId",
      "debitAtomic",
      "creditAtomic",
      "settledAt",
    ],
    code,
  );
  if (
    value.schemaVersion !== SCHEMA_VERSION ||
    value.quoteId !== request.quoteId ||
    !["listing-purchase", "unit-transfer"].includes(value.kind) ||
    typeof value.settlementId !== "string" ||
    !SHA256_PATTERN.test(value.settlementId)
  ) {
    fail(code);
  }
  if (
    value.payerOrgId === value.payeeOrgId &&
    value.debitAtomic !== value.creditAtomic
  ) {
    fail(code);
  }
  return Object.freeze({
    schemaVersion: SCHEMA_VERSION,
    settlementId: value.settlementId,
    quoteId: value.quoteId,
    kind: value.kind,
    payerOrgId: normalizeOrgId(value.payerOrgId, code),
    payeeOrgId: normalizeOrgId(value.payeeOrgId, code),
    debitAtomic: normalizeAtomic(value.debitAtomic, code),
    creditAtomic: normalizeAtomic(value.creditAtomic, code),
    settledAt: normalizeTimestamp(value.settledAt, code),
  });
}

function currentUid(authInstance) {
  const uid = authInstance?.currentUser?.uid;
  if (
    typeof uid !== "string" ||
    uid.length < 1 ||
    uid.length > 128 ||
    CONTROL_CHARACTERS.test(uid)
  ) {
    fail("unauthenticated", "Sign in again to continue.");
  }
  return uid;
}

function normalizeServiceError(error, abortedReason = "stale-quote") {
  if (error instanceof MonetaryAuthorityServiceError) return error;
  const messages = {
    aborted: "This payment changed. Request a new quote and try again.",
    "deadline-exceeded": "The payment service did not respond in time.",
    "failed-precondition": "This payment cannot be completed.",
    "invalid-argument": "The payment request was invalid.",
    "not-found": "This payment is no longer available.",
    "permission-denied": "You cannot make this payment.",
    "stale-auth-context": "The signed-in account changed during the payment.",
    "ui-deadline": "The payment service did not respond in time.",
    unauthenticated: "Sign in again to continue.",
    unavailable: "The payment service is temporarily unavailable.",
  };
  const candidateCode = String(error?.code || "unknown").split("/").pop();
  const code = Object.hasOwn(messages, candidateCode) ? candidateCode : "unknown";
  return new MonetaryAuthorityServiceError(
    code,
    messages[code],
    classifyServerFailure(code, error?.message, abortedReason),
  );
}

export function createMonetaryAuthorityOperationId(
  cryptoProvider = globalThis.crypto,
) {
  if (typeof cryptoProvider?.randomUUID === "function") {
    return cryptoProvider.randomUUID().replaceAll("-", "_");
  }
  if (typeof cryptoProvider?.getRandomValues === "function") {
    const bytes = cryptoProvider.getRandomValues(new Uint8Array(24));
    return `money_${Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("")}`;
  }
  fail("failed-precondition", "Secure payment operations are unavailable.");
}

const RECIPIENT_PAGE_SIZE = 50;
const RECIPIENT_CURSOR_PATTERN = /^[0-9a-f]{64}$/;
const MAX_RECIPIENT_NAME_LENGTH = 80;

function normalizeRecipientListRequest(value) {
  requireExactKeys(value, ["orgId", "cursor"]);
  if (
    value.cursor !== null &&
    (typeof value.cursor !== "string" || !RECIPIENT_CURSOR_PATTERN.test(value.cursor))
  ) {
    fail("invalid-argument");
  }
  return Object.freeze({
    schemaVersion: 1,
    orgId: normalizeOrgId(value.orgId),
    cursor: value.cursor,
    limit: RECIPIENT_PAGE_SIZE,
  });
}

// The members of the acting organisation the actor can pay, without the actor.
function normalizeRecipientListResponse(value, request) {
  if (
    !hasExactKeys(value, ["schemaVersion", "orgId", "recipients", "nextCursor", "hasMore"]) ||
    value.schemaVersion !== 1 ||
    value.orgId !== request.orgId ||
    !Array.isArray(value.recipients) ||
    value.recipients.length > RECIPIENT_PAGE_SIZE ||
    typeof value.hasMore !== "boolean" ||
    (value.hasMore
      ? typeof value.nextCursor !== "string" || !RECIPIENT_CURSOR_PATTERN.test(value.nextCursor)
      : value.nextCursor !== null)
  ) {
    fail("invalid-response");
  }
  const seen = new Set();
  const recipients = value.recipients.map((entry) => {
    if (
      !hasExactKeys(entry, ["publicIdentifier", "displayName"]) ||
      typeof entry.publicIdentifier !== "string" ||
      !PUBLIC_IDENTIFIER_PATTERN.test(entry.publicIdentifier) ||
      seen.has(entry.publicIdentifier) ||
      typeof entry.displayName !== "string" ||
      entry.displayName.length < 1 ||
      entry.displayName.length > MAX_RECIPIENT_NAME_LENGTH
    ) {
      fail("invalid-response");
    }
    seen.add(entry.publicIdentifier);
    return Object.freeze({
      publicIdentifier: entry.publicIdentifier,
      displayName: entry.displayName,
    });
  });
  return Object.freeze({
    recipients: Object.freeze(recipients),
    nextCursor: value.nextCursor,
    hasMore: value.hasMore,
  });
}

export function createMonetaryAuthorityService({
  authInstance = auth,
  functionsInstance = functions,
  callableFactory = httpsCallable,
  quotesEnabled = isMonetaryAuthorityQuotesFeatureEnabled,
  settlementEnabled = isMonetaryAuthoritySettlementFeatureEnabled,
} = {}) {
  const callables = new Map();
  const callableFor = (name) => {
    if (!callables.has(name)) {
      callables.set(name, callableFactory(functionsInstance, name));
    }
    return callables.get(name);
  };
  const invoke = async (name, request, enabled, phase, abortedReason) => {
    if (!enabled) fail("feature-disabled");
    const expectedUid = currentUid(authInstance);
    try {
      const response = await withUiDeadline(callableFor(name)(request), {
        phase,
        timeoutMs: 30_000,
      });
      if (currentUid(authInstance) !== expectedUid) fail("stale-auth-context");
      return response?.data;
    } catch (error) {
      throw normalizeServiceError(error, abortedReason);
    }
  };

  return Object.freeze({
    // Lists the acting organisation's other members so the actor can choose
    // who to pay. Available to every active member of that organisation.
    async listTransferRecipients(optionsValue) {
      const request = normalizeRecipientListRequest(optionsValue);
      const value = await invoke(
        "listUnitTransferRecipients",
        request,
        quotesEnabled,
        "loading members",
      );
      return normalizeRecipientListResponse(value, request);
    },

    async quoteListingPurchase(intentValue) {
      const request = normalizePurchaseRequest(intentValue);
      const value = await invoke(
        "quoteListingPurchase",
        request,
        quotesEnabled,
        "quoting purchase",
      );
      return normalizeQuoteResponse(value, request, "listing-purchase");
    },

    async quoteUnitTransfer(intentValue) {
      const request = normalizeTransferRequest(intentValue);
      const value = await invoke(
        "quoteUnitTransfer",
        request,
        quotesEnabled,
        "quoting transfer",
      );
      return normalizeQuoteResponse(value, request, "unit-transfer", {
        expectedPayeeOrgId: request.toOrgId,
      });
    },

    // Pays the owner of an item placement without naming them. The response
    // discloses only the payee organisation, which the viewer already sees.
    async quoteItemTransfer(intentValue) {
      const request = normalizeItemTransferRequest(intentValue);
      const value = await invoke(
        "quoteItemTransfer",
        request,
        quotesEnabled,
        "quoting item transfer",
      );
      return normalizeQuoteResponse(value, request, "unit-transfer");
    },

    async settleUnitQuote(intentValue) {
      const request = normalizeSettlementRequest(intentValue);
      const value = await invoke(
        "settleUnitQuote",
        request,
        settlementEnabled,
        "settling payment",
      );
      return normalizeSettlementResponse(value, request);
    },

    // Moves the actor's own Units between Current and Savings in one
    // organisation. Idempotent per operationId.
    async moveUnitSavings(intentValue) {
      const request = normalizeSavingsMoveRequest(intentValue);
      const value = await invoke(
        "moveUnitSavings",
        request,
        settlementEnabled,
        "moving Savings",
        "retryable-conflict",
      );
      return normalizeLifetimeResponse(value, request, "savings-move");
    },

    // Removes the actor's expired Units so that later payments and Savings
    // moves are accepted again. Idempotent per operationId.
    async reconcileUnitExpiry(intentValue) {
      const request = normalizeExpiryRequest(intentValue);
      const value = await invoke(
        "reconcileUnitExpiry",
        request,
        settlementEnabled,
        "tidying expired Units",
        "retryable-conflict",
      );
      return normalizeLifetimeResponse(value, request, "expiry");
    },
  });
}

export const monetaryAuthorityService = createMonetaryAuthorityService();
